<template>
    <GoogleMapLoader
            :mapConfig="mapConfig"
            ref="map"
    >
        <template slot-scope="{ google, map }">
            <GoogleMapMarker
                    v-for="marker in markers"
                    :key="marker.id"
                    :marker="marker"
                    :google="google"
                    :map="map"
            />
            <GoogleMapMarker
                    v-for="peer in peerMarkers"
                    :key="peer.id"
                    :marker="peer"
                    :google="google"
                    :map="map"
            />
            <GoogleMapMarker
                    v-if="myLocation"
                    :marker="myLocation"
                    :google="google"
                    :map="map"
            />
        </template>
    </GoogleMapLoader>
</template>

<script>
    import GoogleMapLoader from "../components/GoogleMap/GoogleMapLoader";
    import {avatarColor, escapeHtml, initials, mapSettings, svgAvatar} from "../assets/functions";
    import GoogleMapMarker from "../components/GoogleMap/GoogleMapMarker";
    import {reportLocation} from "../assets/locationReporter";

    // How often the others are picked up. Reporting is not this page's job - it runs
    // app-wide for as long as anyone is logged in.
    const PEER_REFRESH_INTERVAL = 30000;

    export default {
        name: "OrdersMap",
        components: {GoogleMapMarker, GoogleMapLoader},
        data() {
            return {
                markers: [],
                location: null,
                watchID: null,
                peers: [],
                peersTimer: null,
            }
        },


        computed: {
            // The others, as circles carrying their initials. Clicking one opens their
            // full name and how long ago they were last on the map.
            peerMarkers() {
                return this.peers.map(peer => ({
                    id: "user-" + peer.id,
                    position: {lat: peer.last_lat, lng: peer.last_long},
                    icon: {
                        url: svgAvatar(initials(peer.name), avatarColor(peer.id)),
                        // Centred on the position: a circle has no tip to stand on.
                        anchor: {x: 20, y: 20},
                        scaledSize: {width: 40, height: 40},
                    },
                    title: peer.name,
                    zIndex: 9,
                    infoWindow: `<h3 class="title is-6 mb-1">${escapeHtml(peer.name)}</h3>`
                        + `<p class="has-text-grey">${escapeHtml(this.$moment(peer.last_seen_at).fromNow())}</p>`,
                }));
            },
            myLocation() {
                if (!this.location) return null;
                return {
                    id: "GPS",
                    position: this.location,
                    center:true,
                    icon: {url: "https://admin.dnp.mk/img/icons/favicon-32x32.png"},
                    zIndex: 10,
                }
            },
            mapConfig() {
                return {
                    ...mapSettings,
                    disableDefaultUI: true,
                    gestureHandling: "cooperative",
                    center: {
                        lat: 41.1841091,
                        lng: 22.4798533
                    },
                }
            },
            apiKey() {
                return "mapsApiKey";
            },
        },
        mounted() {
            this.fetchOrders();
            this.fetchPeers();
            this.peersTimer = setInterval(this.fetchPeers, PEER_REFRESH_INTERVAL);
            this.watchLocation();
        },
        beforeDestroy() {
            clearInterval(this.peersTimer);
            if (this.watchID !== null) navigator.geolocation.clearWatch(this.watchID);
        },
        methods: {
            watchLocation() {
                // A live watch only while this page is open, so the courier's own dot
                // keeps up with the van. $watchLocation wraps watchPosition in a promise,
                // so it settles on the first fix and hands back the promise instead of a
                // watch id - this page needs every fix and a real id to clear.
                if (!("geolocation" in navigator)) return;
                this.watchID = navigator.geolocation.watchPosition(
                    position => {
                        this.location = {lat: position.coords.latitude, lng: position.coords.longitude};
                        // The reporter owns the throttle, so a fix taken here for the
                        // marker also serves as this minute's report and spares the
                        // app-level poll a second trip to the GPS.
                        reportLocation(this.location);
                    },
                    // Silent: the app-level reporter is the one place that says a fix
                    // cannot be had, and it is already running on this page too.
                    null,
                    {enableHighAccuracy: true, maximumAge: 0}
                );
            },
            fetchPeers() {
                this.$http.get("/map/locations")
                    .then(a => {
                        // The api interceptor resolves with undefined on a handled error;
                        // keep the markers that are on screen rather than dropping them.
                        if (!a) return;
                        this.peers = a.data.data;
                    })
            },
            fetchOrders(){
                this.$http.get("/orders"+this.$urify({...this.$route.query, timestamp:new Date().getTime(),  print:true}))
                    .then(a => {
                      const withoutCoords = a.data.data.orders.filter(a=> !a.address.lat || !a.address.long)
                        if(withoutCoords.length){
                          this.$toast({type:'is-danger', message:`${withoutCoords.length} нарачки немаат кординати`})
                        }
                        this.markers  = a.data.data.orders
                            .filter(a=> a.address.lat && a.address.long)
                            .map(a=>{
                            const pos =  {
                                lat:a.address.lat,
                                lng:a.address.long,
                            };

                            let obj = {
                                id: a.id,
                                position: pos,
                                title:a.user.name,
                                label:a.shipping_order,
                                orderInfoWindow:true,
                                order:a,
                                important: !!a.description
                            }
                            let color;
                            switch (a.current_status) {
                                case "DONE" : color = "35AC5E"; break;
                                case "PACKED" : color = "FFDD57"; break;
                                case "SUBMITTED" :
                                case "CREATED" : color = "363636";break;
                                case "CANCELED" :
                                case "RETURNED" : color = "FF3860";break;
                                default: color = "174287";
                            }
                            Object.assign(obj, {iconColor: color})
                            return obj;
                        });
                    })
            },
        }
    }
</script>
<style scoped>
    .google-map {
        position: relative;
        height: calc(100vh - 3.25rem);
    }

    body {
        overflow: hidden;
    }

</style>
